import classNames from 'classnames';
import { bool, object, string } from 'prop-types';
import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import { compose } from 'redux';

import appSettings from '../../../config/settings';
import { isTooManyRequestsError } from '../../../util/errors';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';

import {
  FieldCurrencyInput,
  FieldTextInput,
  Form,
  Heading,
  IconInquiry,
  PrimaryButton,
} from '../../../components';

import css from './InquiryOfferForm.module.css';

const ErrorMessage = props => {
  const { error } = props;
  // No transaction process attached to listing
  return error ? (
    <p className={css.error}>
      {error.message === 'No transaction process attached to listing' ? (
        <FormattedMessage id="InquiryOfferForm.sendInquiryErrorNoProcess" />
      ) : isTooManyRequestsError(error) ? (
        <FormattedMessage id="InquiryOfferForm.tooManyRequestsError" />
      ) : (
        <FormattedMessage id="InquiryOfferForm.sendInquiryError" />
      )}
    </p>
  ) : null;
};

// NOTE: this InquiryOfferForm is only for booking & purchase processes
// The default-inquiry process is handled differently
const InquiryOfferFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        submitButtonWrapperClassName,
        formId,
        handleSubmit,
        inProgress,
        intl,
        listingTitle,
        authorDisplayName,
        sendInquiryError,
        isIframe,
        config,
      } = fieldRenderProps;

      const messageLabel = intl.formatMessage(
        {
          id: 'InquiryOfferForm.messageLabel',
        },
        { authorDisplayName }
      );
      const messagePlaceholder = intl.formatMessage(
        {
          id: 'InquiryOfferForm.messagePlaceholder',
        },
        { authorDisplayName }
      );

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = submitInProgress;

      return (
        <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="OrderDetailsPage">
          {!isIframe && <IconInquiry className={css.icon} />}
          <Heading as="h2" rootClassName={css.heading}>
            <FormattedMessage id="InquiryOfferForm.heading" values={{ listingTitle }} />
          </Heading>
          <FieldCurrencyInput
            className={css.field}
            type="number"
            name="offer"
            id={formId ? `${formId}.offer` : 'offer'}
            label={intl.formatMessage({ id: 'InquiryOfferForm.offerLabel' })}
            placeholder={intl.formatMessage({ id: 'InquiryOfferForm.offerPlaceholder' })}
            currencyConfig={appSettings.getCurrencyFormatting(config.currency)}
            validate={validators.required(
              intl.formatMessage({ id: 'InquiryOfferForm.offerRequired' })
            )}
          />
          <FieldTextInput
            className={css.field}
            type="textarea"
            name="message"
            id={formId ? `${formId}.message` : 'message'}
            label={messageLabel}
            placeholder={messagePlaceholder}
          />
          <div className={submitButtonWrapperClassName}>
            <ErrorMessage error={sendInquiryError} />
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="InquiryOfferForm.submitButtonText" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

InquiryOfferFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
  isIframe: false,
  sendInquiryError: null,
  config: null,
};

InquiryOfferFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  inProgress: bool,
  isIframe: bool,

  listingTitle: string.isRequired,
  authorDisplayName: string.isRequired,
  sendInquiryError: propTypes.error,
  config: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const InquiryOfferForm = compose(injectIntl)(InquiryOfferFormComponent);

InquiryOfferForm.displayName = 'InquiryOfferForm';

export default InquiryOfferForm;
